import { useMsal } from '@azure/msal-react';
import {
  Button,
  mergeClasses,
  SearchBox,
  Text
} from '@fluentui/react-components';
import { clone } from 'es-toolkit';
import { mapAsync } from 'es-toolkit/array';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useCommonStyles, useStores } from '../../common/hooks';
import { AwardForStaff, AwardGroup, AwardGroupForStaff, AwardGroupRef, AwardLead, blankAwardLead, blankgroupLead, compareAwardForStaff, compareAwardGroupForStaff, copyAwardForStaff, createAwardExecutiveSummaryFileTemplate, GroupLead, JudgingQuestion } from '../../models';
import { CustomMessageBar, PageTitle } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import { LeadAssignment } from '../shared-components/lead-assignment/LeadAssignment';
import Loading from '../shared-components/Loading';
import { Prompt } from '../shared-components/Prompt';
import { useAppToast } from '../../shared/toast';
import { AwardDetailForm } from './components/AwardDetailForm';
import StaffAwardGroupList from './components/StaffAwardGroupList';
import './EditAwardDetailPage.scss';

const EditAwardDetailPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    awardStore,
    editAwardDetailPageUiStore
  } = useStores();

  const commonStyles = useCommonStyles();

  const { showToast } = useAppToast();

  const [isLoading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isShowSaveSuccessToast, setShowSaveSuccessToast] = useState(false);

  const editedAwardForStaff = editAwardDetailPageUiStore.editedAwardForStaff;
  const editedAwardGroupForStaff = editAwardDetailPageUiStore.editedAwardGroupForStaff;

  const saveEditedAward = (): void => {
    if (!editedAwardForStaff) return;
    const isAwardEdited = !compareAwardForStaff(editedAwardForStaff, editAwardDetailPageUiStore.selectedAwardForStaff!);

    const isPreviousAwardUpdated = editAwardDetailPageUiStore.updatedAwardsForStaff.some(awardForStaff => awardForStaff.award.awardId === editedAwardForStaff.award.awardId);
    if (isAwardEdited && !isPreviousAwardUpdated) {
      editAwardDetailPageUiStore.addUpdatedAwardForStaff(editedAwardForStaff);
    }
    else if (!isAwardEdited && isPreviousAwardUpdated) {
      const index = editAwardDetailPageUiStore.updatedAwardsForStaff.findIndex(awardForStaff =>
        awardForStaff.award.awardId === editedAwardForStaff.award.awardId);
      editAwardDetailPageUiStore.removeUpdatedAwardForStaffAt(index);
    }
  }

  const saveEditedGroup = (): void => {
    if (editedAwardGroupForStaff) {
      const isAwardGroupEdited = !compareAwardGroupForStaff(editedAwardGroupForStaff, editAwardDetailPageUiStore.selectedAwardGroupForStaff!);

      const isPreviousAwardGroupUpdated = editAwardDetailPageUiStore.updatedAwardGroupsForStaff
        .some(awardGroupForStaff => awardGroupForStaff.awardGroupId === editedAwardGroupForStaff.awardGroupId);

      if (isAwardGroupEdited && !isPreviousAwardGroupUpdated) {
        editAwardDetailPageUiStore.addUpdatedAwardGroupForStaff(editedAwardGroupForStaff);
      }
      else if (!isAwardGroupEdited && isPreviousAwardGroupUpdated) {
        const index = editAwardDetailPageUiStore.updatedAwardGroupsForStaff.findIndex(awardGroupForStaff =>
          awardGroupForStaff.awardGroupId === editedAwardGroupForStaff.awardGroupId);
        editAwardDetailPageUiStore.removeUpdatedAwardGroupForStaffAt(index);
      }
    }
  }

  const handleAwardGroupSelected = async (selectedGroup: AwardGroup) => {
    if (selectedGroup.awardGroupId === editAwardDetailPageUiStore.selectedAwardGroupForStaff?.awardGroupId) return;
    setLoading(true);
    const updatedAwardGroupForStaff = editAwardDetailPageUiStore.updatedAwardGroupsForStaff.find(
      awardGroupForStaff => awardGroupForStaff.awardGroupId === selectedGroup.awardGroupId);

    saveEditedAward();
    saveEditedGroup();

    const groupLeads = await apiStore.getGroupLeads(instance, selectedGroup.awardGroupId);
    const awardGroupForStaff: AwardGroupForStaff = {
      awardGroupId: selectedGroup.awardGroupId,
      title: selectedGroup.title,
      groupLeads,
    };

    editAwardDetailPageUiStore.setSelectedAwardForStaff(undefined);
    editAwardDetailPageUiStore.setEditedAwardForStaff(undefined);
    editAwardDetailPageUiStore.setEditedAwardGroupForStaff(updatedAwardGroupForStaff ?? clone(awardGroupForStaff));
    editAwardDetailPageUiStore.setSelectedAwardGroupForStaff(awardGroupForStaff);
    setLoading(false);
  }

  const handleAwardSelected = async (selectedAward: AwardGroupRef): Promise<void> => {
    try {
      if (selectedAward.awardId === editedAwardForStaff?.award.awardId) return;
      setLoading(true);
      const updatedAwardForStaff = editAwardDetailPageUiStore.updatedAwardsForStaff.find(
        awardForStaff => awardForStaff.award.awardId === selectedAward.awardId);

      saveEditedAward();
      saveEditedGroup();

      const [award, judgingQuestions, awardLeads, awardExecutiveSummaryFileTemplate, _] = await Promise.all([
        apiStore.getAwardById(instance, selectedAward.awardId),
        apiStore.getJudgingQuestions(instance, selectedAward.awardId),
        apiStore.getAwardLeads(instance, selectedAward.awardId),
        apiStore.getAwardExecutiveSummaryFileTemplateMetadata(instance, selectedAward.awardId),
        getAwardJudgeCapacityOptions(selectedAward.awardId),
      ]);

      const awardForStaff: AwardForStaff = {
        award,
        awardLeads,
        judgingQuestions,
        awardExecutiveSummaryFileTemplate
      };

      editAwardDetailPageUiStore.setSelectedAwardGroupForStaff(undefined);
      editAwardDetailPageUiStore.setEditedAwardGroupForStaff(undefined);
      editAwardDetailPageUiStore.setSelectedAwardForStaff(awardForStaff);
      editAwardDetailPageUiStore.setEditedAwardForStaff(updatedAwardForStaff ?? copyAwardForStaff(awardForStaff));
    } catch (error) {
      // TODO - handle error
    } finally {
      setLoading(false);
    }
  }

  const handleAddAwardLead = (emailAddress: string, isPrimary: boolean) => {
    const newAwardLead = blankAwardLead();
    newAwardLead.emailAddress = emailAddress;
    newAwardLead.awardId = editedAwardForStaff!.award.awardId;
    newAwardLead.isPrimary = isPrimary;
    editedAwardForStaff?.awardLeads.push(newAwardLead);
  }

  const handleDeleteAwardLead = (awardLead: AwardLead): void => {
    editedAwardForStaff?.awardLeads.splice(editedAwardForStaff.awardLeads.indexOf(awardLead), 1);
    if (awardLead.awardLeadId > 0)
      editAwardDetailPageUiStore.addAwardLeadToDelete(awardLead);
  }

  const handleAddGroupLead = (emailAddresses: string[]) => {
    emailAddresses.forEach(emailAddress => {
      const newGroupLead = blankgroupLead();
      newGroupLead.emailAddress = emailAddress;
      newGroupLead.groupId = editedAwardGroupForStaff?.awardGroupId!;
      newGroupLead.isPrimary = false;
      editedAwardGroupForStaff?.groupLeads.push(newGroupLead);
    });
  }

  const handleDeleteGroupLead = (groupLead: GroupLead): void => {
    editedAwardGroupForStaff?.groupLeads.splice(editedAwardGroupForStaff.groupLeads.indexOf(groupLead), 1);
    if (groupLead.groupLeadId > 0)
      editAwardDetailPageUiStore.addGroupLeadToDelete(groupLead);
  }

  const handleAddAwardTemplate = (content: File) => {
    const executiveSummaryFileTemplate = createAwardExecutiveSummaryFileTemplate(editedAwardForStaff!.award.awardId, content);
    editedAwardForStaff!.awardExecutiveSummaryFileTemplate = executiveSummaryFileTemplate;
  }

  const handleDeleteAwardTemplate = (): void => {
    if (editedAwardForStaff?.awardExecutiveSummaryFileTemplate?.executiveSummaryFileTemplateId! > 0) {
      editAwardDetailPageUiStore.addAwardTemplateToDelete(editedAwardForStaff!.awardExecutiveSummaryFileTemplate!);
    }
    editedAwardForStaff!.awardExecutiveSummaryFileTemplate = undefined;
  }

  const upsertJudgingQuestions = async (awardId: number, judgingQuestions: JudgingQuestion[]) => {
    try {
      await apiStore.upsertJudgingQuestions(instance, awardId, judgingQuestions);
    } catch (error) {
      // TODO - handle error
    }
  };

  const saveAwardForStaff = async (updatedAwardForStaff: AwardForStaff): Promise<void> => {
    const awardId = updatedAwardForStaff.award.awardId;

    await apiStore.updateAward(instance, awardId, {
      masterAwardId: 0,
      programId: 0,
      groupId: updatedAwardForStaff.award.groupId,
      title: updatedAwardForStaff.award.title,
      summary: updatedAwardForStaff.award.summary,
      detail: updatedAwardForStaff.award.detail,
      requireWrittenNomination: updatedAwardForStaff.award.requireWrittenNomination,
      requireCustomerReference: updatedAwardForStaff.award.requireCustomerReference,
      allowJointNomination: updatedAwardForStaff.award.allowJointNomination,
      maxWinners: updatedAwardForStaff.award.maxWinners,
      maxFinalists: updatedAwardForStaff.award.maxFinalists,
      eligibilityRuleCodes: updatedAwardForStaff.award.eligibilityRuleCodes,
    });
    await updateAwardConfiguration(awardId, updatedAwardForStaff.award.judgeCapacity, updatedAwardForStaff.award.defaultScrubbingRubric);

    if (updatedAwardForStaff.awardExecutiveSummaryFileTemplate?.content !== undefined && !updatedAwardForStaff.award.requireWrittenNomination)
      await apiStore.uploadAwardExecutiveSummaryFileTemplate(instance, awardId, updatedAwardForStaff.awardExecutiveSummaryFileTemplate.content);
    else if (updatedAwardForStaff.awardExecutiveSummaryFileTemplate && updatedAwardForStaff.award.requireWrittenNomination)
      await apiStore.deleteAwardExecutiveSummaryFileTemplate(instance, awardId);
    await Promise.all(
      updatedAwardForStaff.awardLeads
        .filter(awardLead => awardLead.awardLeadId === 0)
        .map(awardLead => apiStore.assignAwardLeads(instance, awardId, awardLead))
    );
    if (updatedAwardForStaff.judgingQuestions.length > 0) await upsertJudgingQuestions(awardId, updatedAwardForStaff.judgingQuestions);
  }

  const saveAwardGroupForStaff = async (updatedAwardGroupForStaff: AwardGroupForStaff): Promise<void> => {
    await Promise.all(
      updatedAwardGroupForStaff.groupLeads
        .filter(groupLead => groupLead.groupLeadId === 0)
        .map(groupLead => apiStore.assignGroupLeads(instance, updatedAwardGroupForStaff.awardGroupId, groupLead))
    );
  }

  const deletePendingChanges = async (): Promise<void> => {
    await Promise.all([
      mapAsync(
        editAwardDetailPageUiStore.awardLeadsToDelete,
        async (awardLead) => {
          await apiStore.deleteAwardLeads(instance, awardLead.awardLeadId);
          editAwardDetailPageUiStore.removeAwardLeadToDelete(awardLead.awardLeadId);
        },
      ),
      mapAsync(
        editAwardDetailPageUiStore.groupLeadsToDelete,
        async (groupLead) => {
          await apiStore.deleteGroupLeads(instance, groupLead.groupLeadId);
          editAwardDetailPageUiStore.removeGroupLeadToDelete(groupLead.groupLeadId);
        },
      ),
      mapAsync(
        editAwardDetailPageUiStore.awardTemplatesToDelete,
        async (awardTemplate) => {
          await apiStore.deleteAwardExecutiveSummaryFileTemplate(instance, awardTemplate.awardId);
          editAwardDetailPageUiStore.removeAwardTemplateToDelete(awardTemplate.awardId);
        },
      ),
      mapAsync(
        editAwardDetailPageUiStore.judgingQuestionsToDelete,
        async (judgingQuestion) => {
          await apiStore.deleteJudgingQuestion(instance, judgingQuestion.awardId, judgingQuestion.questionId);
          editAwardDetailPageUiStore.removeJudgingQuestionToDelete(judgingQuestion.awardId, judgingQuestion.questionId);
        },
      ),
    ]);
  };

  const shouldBlocking = (!editedAwardForStaff ||
    !editAwardDetailPageUiStore.selectedAwardForStaff ||
    (editAwardDetailPageUiStore.updatedAwardsForStaff.length > 0 ||
      !compareAwardForStaff(editedAwardForStaff, editAwardDetailPageUiStore.selectedAwardForStaff))) ||
    (!editedAwardGroupForStaff ||
      !editAwardDetailPageUiStore.selectedAwardGroupForStaff ||
      (editAwardDetailPageUiStore.updatedAwardGroupsForStaff.length > 0 ||
        !compareAwardGroupForStaff(editedAwardGroupForStaff, editAwardDetailPageUiStore.selectedAwardGroupForStaff)));

  const handleSaveAllChanges = async (): Promise<void> => {
    editAwardDetailPageUiStore.setIsBeingValidated(true);

    if (editedAwardForStaff && !editAwardDetailPageUiStore.totalJudgingQuestionWeight.isValid) {
      showToast({
        title: `Set the total weight to 100%. The current total is ${editAwardDetailPageUiStore.totalJudgingQuestionWeight.value}%.`,
        intent: 'error',
      });
    }

    if (editedAwardForStaff && !editAwardDetailPageUiStore.isJudgingQuestionValid) return;

    if ((!editedAwardForStaff ||
      (editAwardDetailPageUiStore.updatedAwardsForStaff.length === 0 &&
        compareAwardForStaff(editedAwardForStaff!, editAwardDetailPageUiStore.selectedAwardForStaff!))) &&
      (!editedAwardGroupForStaff ||
        (editAwardDetailPageUiStore.updatedAwardGroupsForStaff.length === 0 &&
          compareAwardGroupForStaff(editedAwardGroupForStaff!, editAwardDetailPageUiStore.selectedAwardGroupForStaff!)))) return;

    setLoading(true);

    const handleSaveSuccess = () => {
      editAwardDetailPageUiStore.resetAwardForStaffData();
      setShowSaveSuccessToast(true);
      setTimeout(() => {
        setShowSaveSuccessToast(false);
      }, 4000);
    };

    try {
      await deletePendingChanges();

      if (editedAwardForStaff && !compareAwardForStaff(editedAwardForStaff!, editAwardDetailPageUiStore.selectedAwardForStaff!) &&
        (!editAwardDetailPageUiStore.updatedAwardsForStaff
          .some(updatedAwardForStaff => updatedAwardForStaff.award.awardId === editedAwardForStaff.award.awardId) ||
          editAwardDetailPageUiStore.updatedAwardsForStaff.length === 0)) {
        editAwardDetailPageUiStore.addUpdatedAwardForStaff(editedAwardForStaff);
      }
      if (editedAwardGroupForStaff && !compareAwardGroupForStaff(editedAwardGroupForStaff!, editAwardDetailPageUiStore.selectedAwardGroupForStaff!) &&
        (!editAwardDetailPageUiStore.updatedAwardGroupsForStaff
          .some(updatedAwardGroupForStaff => updatedAwardGroupForStaff.awardGroupId === editedAwardGroupForStaff.awardGroupId) ||
          editAwardDetailPageUiStore.updatedAwardGroupsForStaff.length === 0)) {
        editAwardDetailPageUiStore.addUpdatedAwardGroupForStaff(editedAwardGroupForStaff);
      }

      await Promise.all([
        ...editAwardDetailPageUiStore.updatedAwardsForStaff.map(updatedAwardForStaff => saveAwardForStaff(updatedAwardForStaff)),
        ...editAwardDetailPageUiStore.updatedAwardGroupsForStaff.map(updatedAwardGroupForStaff => saveAwardGroupForStaff(updatedAwardGroupForStaff))
      ]);
      handleSaveSuccess();
    } catch (error) {
      // TODO - handle error
    } finally {
      setLoading(false);
    }
  }

  const debouncedSearch = useDebouncedCallback(
    async (query: string) => {
      setLoading(true);
      try {
        const awardGroups = await apiStore.getAwardGroups(instance, { searchText: query });
        editAwardDetailPageUiStore.setAwardGroups(awardGroups);
      }
      finally {
        setLoading(false);
      }
    },
    1000
  );

  const handleSearchTextChanged = (value: string) => {
    setSearchText(value);
    debouncedSearch(value);
  }

  const getAdminAwardGroups = async (includeCountryAward: boolean): Promise<void> => {
    try {
      const awardGroups = await apiStore.getAdminAwardGroups(instance, { includeCountryAward });
      editAwardDetailPageUiStore.setAwardGroups(awardGroups);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getAwardJudgeCapacityOptions = async (awardId: number): Promise<void> => {
    try {
      const judgeCapacityOptions = await apiStore.getAwardJudgeCapacityOptions(instance, awardId);
      editAwardDetailPageUiStore.setJudgeCapacityOptions(judgeCapacityOptions);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getSpecializationDesignations = async (): Promise<void> => {
    try {
      const specializationDesignations = await apiStore.getSpecializationPrograms(instance);
      editAwardDetailPageUiStore.setSpecializationDesignations(specializationDesignations);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getDesignations = async (): Promise<void> => {
    try {
      const designationOptions = await apiStore.getDesignationProgramOptions(instance);
      editAwardDetailPageUiStore.setDesignationOptions(designationOptions);
    } catch (error) {
      // TODO - handle error
    }
  }

  const getEligibilityRules = async (): Promise<void> => {
    try {
      const rules = await apiStore.getEligibilityRules(instance, 'Category');
      awardStore.setEligibilityRules('Category', rules);
    } catch (error) {
      // TODO - handle error
    }
  };

  const updateAwardConfiguration = async (awardId: number, judgeCapacity: number, defaultScrubbingRubric?: string): Promise<void> => {
    try {
      await apiStore.updateAwardConfiguration(instance, awardId, { judgeCapacity, defaultScrubbingRubric });
    } catch (error) {
      // TODO - handle error
    }
  }

  const preloadData = async (): Promise<void> => {
    setLoading(true);

    await Promise.allSettled([
      getAdminAwardGroups(true),
      getEligibilityRules(),
      getDesignations(),
      getSpecializationDesignations(),
    ]);

    setLoading(false);
  }

  useEffect(() => {
    preloadData();
    return () => {
      editAwardDetailPageUiStore.resetStates();
    }
  }, []);

  useEffect(() => () => {
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  return (
    <FlexBox
      isFullWidth
      direction='column'
      className='edit-award-detail-page'
    >
      <Prompt
        when={shouldBlocking}
      />
      {
        isShowSaveSuccessToast &&
        <CustomMessageBar
          message={'Awards successfully saved'}
          notificationTypeCode='success'
          showDismissButton
        />
      }
      {isLoading && <Loading />}
      <FlexBox
        isFullWidth
        content='space-between'
        aligns='end'
        className='edit-award-detail-page__hero'
      >
        <PageTitle
          title='Edit Award Detail'
          subtitle={awardStore.defaultProgramRef?.displayName}
        />
        <Button
          appearance='primary'
          onClick={handleSaveAllChanges}
        >
          Save all
        </Button>
      </FlexBox>
      <FlexBox
        isFullWidth
        className='edit-award-detail-page__main'
      >
        <div className='main-sidebar'>
          <div className='main-sidebar__search-box'>
            <SearchBox
              id='staff-award-group-search'
              placeholder='Search'
              value={searchText}
              className={mergeClasses(commonStyles.inputField, 'search-box__input')}
              onChange={(_, data) => handleSearchTextChanged(data.value)}
            />
          </div>
          <StaffAwardGroupList
            awardGroups={editAwardDetailPageUiStore.awardGroups}
            onGroupSelected={handleAwardGroupSelected}
            onAwardSelected={handleAwardSelected}
            isAwardUpdated={award => editAwardDetailPageUiStore.updatedAwardsForStaff
              .some(awardForStaff => awardForStaff.award.awardId === award.awardId)}
            isGroupUpdated={awardGroup => editAwardDetailPageUiStore.updatedAwardGroupsForStaff
              .some(awardGroupForStaff => awardGroupForStaff.awardGroupId === awardGroup.awardGroupId)}
          />
        </div>
        {
          editAwardDetailPageUiStore.editedAwardForStaff &&
          <div className='main-content'>
            <Text
              as='h2'
              block
              size={500}
              weight='semibold'
            >
              {editAwardDetailPageUiStore.editedAwardForStaff.award.title}
            </Text>
            <AwardDetailForm
              onAddAwardLead={handleAddAwardLead}
              onDeleteAwardLead={handleDeleteAwardLead}
              onAddAwardTemplate={handleAddAwardTemplate}
              onDeleteAwardTemplate={handleDeleteAwardTemplate}
              onAddJudgingQuestion={() => editAwardDetailPageUiStore.addJudgingQuestion()}
              onDeleteJudgingQuestion={(judgingQuestion) => editAwardDetailPageUiStore.deleteJudgingQuestion(judgingQuestion)}
            />
          </div>
        }
        {
          editAwardDetailPageUiStore.selectedAwardGroupForStaff &&
          <div className='main-content'>
            <Text
              as='h2'
              block
              size={500}
              weight='semibold'
            >
              {editedAwardGroupForStaff?.title}
            </Text>
            <div>
              <LeadAssignment
                leads={editedAwardGroupForStaff?.groupLeads ?? []}
                title='Add leads'
                isPrimaryLead={false}
                allowMultipleEmailAddresses
                onAddLeads={handleAddGroupLead}
                onDeleteLead={lead => handleDeleteGroupLead(lead as GroupLead)}
              />
            </div>
          </div>
        }
        {
          !editAwardDetailPageUiStore.editedAwardForStaff &&
          !editAwardDetailPageUiStore.selectedAwardGroupForStaff &&
          <Text
            as='p'
            className='page-fallback'
          >
            Sorry, no award is currently selected.
          </Text>
        }
      </FlexBox>
    </FlexBox>
  );
}

export const EditAwardDetailPage = observer(EditAwardDetailPageImpl);
